import {
  Children, Fragment, cloneElement, forwardRef, isValidElement,
  type ButtonHTMLAttributes, type HTMLAttributes, type KeyboardEvent, type ReactElement, type ReactNode,
  type Ref, type RefAttributes, type SyntheticEvent,
} from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cx } from '../controls/cx'
import s from './NavigationDestination.module.css'

/**
 * THE RAIL'S DESTINATION — one expanded navigation entry (#2624).
 *
 * ── why this is in the kit ─────────────────────────────────────────────────
 * The Navbar "Chisel Line" sheet (#2406, LOCKED) draws a destination as TEXT
 * hanging from the section's chisel line: rest ink, a tone step on hover and a
 * deeper one on press, and — for the page on screen — brand text, weight 700
 * and the VEIN, the lit `--mat-vein` cut lying on the chisel, 2px and widening
 * to 3px on hover. Both hosts drew it, each in its own sheet: the console's
 * `Nav.module.css` `.item`/`.active` and the portal's `app.module.css`
 * `.navItem[data-active]`, value for value. A solution host given only the
 * shell would have had to copy one of them. So the anatomy is here once, and
 * both hosts draw it through this component (marble-uiux's extraction gate).
 *
 * ── what it must not know ──────────────────────────────────────────────────
 * Like `NavigationShell` and `NavigationSectionHead`, it fetches nothing,
 * routes nothing and decides nothing. A destination grants nothing: a host
 * passes only entries a governed read already returned for this principal,
 * and a present prop is not authority. The label is `children`, already in the
 * reader's language; the component adds no words of its own. The paint keys on
 * the element's ACTUAL `aria-current`, so the announced state and the painted
 * state cannot disagree.
 *
 * ── two shapes, typed as what they render (ROOT #2624 c.93430) ────────────
 * 1. Its OWN native `<button type="button">` — the portal's destinations are
 *    page slugs switched in state, so a button is the honest control. Its
 *    props are button props and its ref is an `HTMLButtonElement`. `current`
 *    (default false) writes `aria-current="page"`; `disabled` is native.
 *
 * 2. ONE element the host SUPPLIES, with `asChild`, through the same Radix
 *    `Slot` `Button` uses. Its props are element props and its ref is the
 *    element's own (`HTMLElement`); nothing is wrapped around it. The element
 *    is the host's: a router's link, or a native element that is a destination
 *    but not navigable here — an announced Soon page, a row being edited in
 *    place that must still preview the destination it will become.
 *
 *    - `current` OMITTED: the host or its router is the SOLE producer of
 *      `aria-current`, and the kit paints whatever it states. The console's
 *      TanStack `<Link>` works this way — the router decides which page is
 *      current, never this component.
 *    - `current` GIVEN (true or false): the kit controls `aria-current`, on a
 *      NATIVE supplied element only, overriding whatever the element said.
 *    - `disabled`: on a NATIVE supplied element only. It DOMINATES: no
 *      `aria-current`, `aria-disabled="true"`, out of the tab order, and every
 *      activation is stopped before the element's own handlers, the
 *      destination's own or a React ancestor's can see it (below). An anchor
 *      loses its `href`, so there is nothing to follow, open in a new tab or
 *      offer in a context menu; a button is natively `disabled` too. The root
 *      must be a native a / button / span / div holding only PASSIVE content
 *      (text, fragments, native label and icon markup) — anything else is
 *      refused, because a root out of the tab order does not make a subtree
 *      inert. The reason it is unavailable is the host's words BESIDE the
 *      destination (a Soon pill in the host's own placement), never a dimmed
 *      colour alone. A capture listener attached outside React, or above
 *      React's root, runs before the guard and is outside what this controls.
 *
 *    A composite (a router component, any non-DOM element) given `disabled` or
 *    an explicit `current` is REFUSED with a thrown error, because what it
 *    renders and how it handles a click are its own: a router spreads its own
 *    `aria-current` last and navigates in its own handler, so the kit could
 *    only PRETEND to control either. Supply a native element instead.
 *
 * ── how the state is controlled, and why not by Slot ───────────────────────
 * Radix `Slot` (1.3.0) merges `{ ...slotProps, ...childProps }`, so a supplied
 * element's own `aria-current` / `tabIndex` / `aria-disabled` would win, and it
 * composes handlers CHILD FIRST, so a disabled link's own `onClick` would run
 * before any kit `preventDefault` (ROOT #2624 c.93413, F1 and F2). So a native
 * supplied element is CLONED with the controlled state before Slot composes
 * it, and — when disabled — its activation capture handlers (click, auxiliary
 * click, Enter and Space) are replaced by the kit's guard, which prevents the
 * default and stops propagation in the capture phase. React dispatches capture
 * from its root, so stopping there means no bubble handler — the element's,
 * the destination's own or an ancestor's — ever runs. Every other handler is
 * left exactly as the host wrote it; refs and placement classes are composed.
 *
 * ── the vein and the chisel ────────────────────────────────────────────────
 * The vein reaches back from the item to lie on the chisel the item hangs
 * from: `left: calc(var(--nav-hang, 0px) * -1)`. `--nav-hang` is the host's
 * PLACEMENT — how far its list indents its items from the line it draws — so
 * it is read here and declared there, and a host that draws no chisel gets
 * the vein on the item's own left cut.
 */

/** The kit's own button: button props, an `HTMLButtonElement` ref. */
export interface NavigationDestinationButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-current' | 'disabled'> {
  asChild?: false
  /** This destination is the page on screen: `aria-current="page"`. Default false. */
  current?: boolean
  /** Offered but not available now: native `disabled`, and never current. */
  disabled?: boolean
  /** The label, in the reader's language. */
  children: ReactNode
}

/** ONE host-supplied element: element props, the element's own ref. */
export interface NavigationDestinationElementProps
  extends Omit<HTMLAttributes<HTMLElement>, 'aria-current' | 'aria-disabled'> {
  asChild: true
  /**
   * Omitted: the host or its router alone states `aria-current`. Given: the
   * kit controls it — on a native supplied element only.
   */
  current?: boolean
  /** Offered but not available now — on a native supplied element only. Dominates `current`. */
  disabled?: boolean
  /** Exactly one element: the host's router link, or a native element it owns. */
  children: ReactElement
}

export type NavigationDestinationProps = NavigationDestinationButtonProps | NavigationDestinationElementProps

/** Typed per shape: the props and the ref of the element actually rendered. */
interface NavigationDestinationComponent {
  (props: NavigationDestinationButtonProps & RefAttributes<HTMLButtonElement>): ReactElement | null
  (props: NavigationDestinationElementProps & RefAttributes<HTMLElement>): ReactElement | null
  displayName?: string
}

const ACTIVATION_KEYS = new Set(['Enter', ' '])

/** Stops an activation in the capture phase: no default action, no handler below or after. */
function stop(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

type KeyCapture = ((event: KeyboardEvent) => void) | undefined

/* ── THE CLOSED PASSIVE-CONTENT CONTRACT (ROOT #2624 c.93468 (3)) ──────────
 * A disabled supplied element is promised to be UNAVAILABLE, and a root that
 * leaves the tab order does not make its subtree inert: a button inside a
 * disabled row is still a button. So the promise is made only where it can be
 * checked from the element tree itself, without rendering or invoking anything:
 * the root is a native a / button / span / div, and everything below it is
 * text, numbers, nothing, arrays, fragments or native passive label and icon
 * markup. Anything else — a control or link, a tab stop, editable content, an
 * interactive role or handler, markup that cannot be inspected, or a component
 * whose output is unknown until it renders — is refused, never advertised as
 * inert. An ENABLED destination is not inspected: an edit row keeps its grip
 * and menu, a router keeps its link. */
const DISABLED_ROOTS = new Set(['a', 'button', 'span', 'div'])
const PASSIVE_MARKUP = new Set([
  'span', 'strong', 'em', 'b', 'i', 'small', 'abbr', 'mark', 'sub', 'sup', 'code', 'kbd', 'time', 'br', 'wbr', 'img',
  'svg', 'g', 'path', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'rect', 'title', 'desc',
])
const PASSIVE_ROLES = new Set(['presentation', 'none', 'img'])

function refuse(what: string): never {
  throw new Error(
    `NavigationDestination: a disabled supplied element may hold only passive content (text, numbers, `
    + `fragments and native label or icon markup), and it holds ${what}. A disabled destination cannot `
    + `make a subtree inert; render the control outside it, or leave the destination enabled.`,
  )
}

/* ── FAIL-CLOSED NAMES (ROOT #2624 c.93517, N5) ─────────────────────────────
 * The types hold TypeScript consumers; a JavaScript, createElement or spread
 * consumer can write any spelling. React forwards an unknown string attribute
 * and HTML folds its name to lower case, so `tabindex` or `ROLE` either
 * smuggles what the canonical check refuses, or lands on the very attribute
 * the destination writes and wins by being written last. So names are matched
 * case-insensitively, and a NONCANONICAL spelling of a name that matters is
 * refused, never forwarded. Nothing a caller passes is stringified or invoked
 * to decide: a role is either absent or an exact primitive string. */

/** What the destination itself writes on a native supplied root. */
const CONTROLLED_NAMES = ['aria-current', 'aria-disabled', 'data-disabled', 'tabIndex', 'href', 'disabled']
/** What makes an element in a disabled subtree active or uninspectable. */
const ACTIVE_NAMES = ['tabIndex', 'contentEditable', 'autoFocus', 'role', 'dangerouslySetInnerHTML']

const canonicalOf = (names: readonly string[]) => new Map(names.map((name) => [name.toLowerCase(), name]))
const CONTROLLED = canonicalOf(CONTROLLED_NAMES)
const ACTIVE = canonicalOf(ACTIVE_NAMES)
/** N7: an img's map reference, in every spelling. */
const USE_MAP = canonicalOf(['useMap'])

/** Throws on the first key that is a different-case spelling of one of `names`. */
function refuseAliases(where: string, props: Record<string, unknown>, names: Map<string, string>) {
  for (const key of Object.keys(props)) {
    const canonical = names.get(key.toLowerCase())
    if (canonical !== undefined && key !== canonical) {
      throw new Error(
        `NavigationDestination: ${where} spells \`${key}\`, a noncanonical alias of \`${canonical}\`. `
        + 'React would forward it beside the destination\'s own attribute and HTML would fold both onto one; '
        + `write \`${canonical}\`.`,
      )
    }
  }
}

/** A present role must be a primitive string — checked before anything could stringify it. */
function roleOf(type: string, props: Record<string, unknown>): string | undefined {
  if (props.role === undefined || props.role === null) return undefined
  if (typeof props.role !== 'string') refuse(`a role that is not a string (<${type} role>)`)
  return props.role as string
}

/**
 * Refuses any property that would make an element in a disabled subtree actionable or uninspectable.
 * `root` keeps the root/descendant distinction: a root may carry a string role, handlers and focus;
 * a descendant may not. `where` names the props being read, for the diagnostic.
 */
function assertPassiveProps(type: string, props: Record<string, unknown>, root: boolean, where?: string) {
  refuseAliases(where ?? (root ? `a disabled <${type}> root` : `a <${type}> inside a disabled destination`), props, ACTIVE)
  if (props.dangerouslySetInnerHTML != null) refuse(`<${type}> markup that cannot be inspected (dangerouslySetInnerHTML)`)
  if (props.contentEditable != null && props.contentEditable !== false && props.contentEditable !== 'false') {
    refuse(`editable content (<${type} contentEditable>)`)
  }
  const role = roleOf(type, props)
  if (root) return
  /* N7 (ROOT c.93562): an image map lives OUTSIDE the inspected subtree, and
     HTML's image-map processing model hands the image's interactions — and
     possibly keyboard focus — to the mapped `area` links. The guard cannot
     promise control of a map it never saw, so an img may not name one. The
     map is never read; any spelling is refused, the canonical one when it is
     present and non-null. */
  if (type === 'img') {
    refuseAliases('an <img> inside a disabled destination', props, USE_MAP)
    if (props.useMap !== undefined && props.useMap !== null) refuse('an image map (<img useMap>), whose links lie outside it')
  }
  if (props.tabIndex != null) refuse(`a tab stop (<${type} tabIndex>)`)
  if (role !== undefined && !PASSIVE_ROLES.has(role)) refuse(`an interactive role (<${type} role="${role}">)`)
  if (props.autoFocus) refuse(`an autofocused element (<${type}>)`)
  for (const key of Object.keys(props)) {
    if (/^on/i.test(key)) refuse(`a handler (<${type} ${key}>)`)
  }
}

function assertPassiveChildren(children: unknown) {
  if (children == null || typeof children === 'boolean' || typeof children === 'string' || typeof children === 'number') return
  if (Array.isArray(children)) { children.forEach(assertPassiveChildren); return }
  if (!isValidElement(children)) refuse('content that is not an element tree (a portal or an unknown object)')
  const element = children as ReactElement<Record<string, unknown>>
  if (element.type === Fragment) { assertPassiveChildren(element.props.children); return }
  if (typeof element.type !== 'string') refuse('a component whose output is unknown until it renders')
  if (!PASSIVE_MARKUP.has(element.type)) refuse(`<${element.type}>, which is not passive label or icon markup`)
  assertPassiveProps(element.type, element.props, false)
  assertPassiveChildren(element.props.children)
}

function assertDisabledRoot(child: ReactElement<Record<string, unknown>>) {
  const type = child.type as string
  if (!DISABLED_ROOTS.has(type)) {
    throw new Error(
      `NavigationDestination: a disabled supplied element must be a native a, button, span or div; <${type}> is not.`,
    )
  }
  assertPassiveProps(type, child.props, true)
  assertPassiveChildren(child.props.children)
}

/** The controlled state a NATIVE supplied element is cloned with, before Slot sees it. */
function controlledState(
  child: ReactElement<Record<string, unknown>>,
  { current, disabled, tabIndex }: { current?: boolean; disabled: boolean; tabIndex?: number },
): Record<string, unknown> {
  if (disabled) {
    const ownKeyCapture = child.props.onKeyDownCapture as KeyCapture
    return {
      'aria-current': undefined,
      'aria-disabled': true,
      'data-disabled': '',
      tabIndex: -1,
      /* No actionable address on a disabled anchor (c.93468 (2)): nothing to
         follow, open in a new tab or offer in a context menu. */
      ...(child.type === 'a' ? { href: undefined } : {}),
      /* A native button is natively disabled as well (c.93468 (2)). */
      ...(child.type === 'button' ? { disabled: true } : {}),
      onClickCapture: stop,
      onAuxClickCapture: stop,
      /* Enter and Space activate; every other key still reaches the element. */
      onKeyDownCapture: (event: KeyboardEvent) => {
        if (ACTIVATION_KEYS.has(event.key)) { stop(event); return }
        ownKeyCapture?.(event)
      },
    }
  }
  return {
    'aria-current': current === undefined ? child.props['aria-current'] : current ? 'page' : undefined,
    'aria-disabled': undefined,
    'data-disabled': undefined,
    ...(tabIndex === undefined ? {} : { tabIndex }),
  }
}

/**
 * The destination's OWN props (N5): no respelling of what it controls, and none
 * of the state it writes itself — neither typed shape admits `aria-current`,
 * the element shape does not admit `aria-disabled`, and `data-disabled` is the
 * kit's paint hook. Such a prop can only arrive from JavaScript, and forwarded
 * through Slot onto a composite it would defeat the controlled state.
 */
function refusePassThrough(props: Record<string, unknown>, asChild: boolean) {
  refuseAliases('the destination\'s own props', props, CONTROLLED)
  const written = asChild ? ['aria-current', 'aria-disabled', 'data-disabled'] : ['aria-current', 'data-disabled']
  for (const name of written) {
    if (name in props) {
      throw new Error(
        `NavigationDestination: \`${name}\` is the destination's own state, not a prop to pass through; `
        + 'use `current` and `disabled`, or omit `current` and let the host\'s router state the page.',
      )
    }
  }
}

function render(props: NavigationDestinationProps, ref: Ref<HTMLElement>) {
  refusePassThrough(props as unknown as Record<string, unknown>, props.asChild === true)
  if (props.asChild) {
    const { asChild: _asChild, current, disabled = false, className, children, tabIndex, ...rest } = props
    const child = Children.only(children)
    const native = isValidElement(child) && typeof child.type === 'string'
    if (!native && (disabled || current !== undefined)) {
      throw new Error(
        'NavigationDestination: `disabled` and an explicit `current` are controlled only on a native DOM element '
        + 'supplied with `asChild` (an <a>, <button>, <span> or <div>). A composite or router component owns its own '
        + 'aria-current and click handling: omit `current` and let the router state it, and supply a native '
        + 'element for a destination that is not available.',
      )
    }
    const element = child as ReactElement<Record<string, unknown>>
    /* A native root may not respell what the destination writes on it, in any
       mode (N5). A composite is never inspected or invoked: it is opaque. */
    if (native) refuseAliases(`a supplied <${element.type as string}>`, element.props, CONTROLLED)
    if (native && disabled) {
      assertDisabledRoot(element)
      /* N6 (ROOT c.93557): Slot composes the destination's OWN props onto that
         element, so they are part of the effective root. They get the same
         root validation before Slot sees them — an own `contentEditable`,
         `dangerouslySetInnerHTML`, non-string `role` or any active alias
         would otherwise reach the disabled root unchecked. */
      assertPassiveProps(element.type as string, rest, true, `the destination's own props on a disabled <${element.type as string}>`)
    }
    const supplied = native ? cloneElement(element, controlledState(element, { current, disabled, tabIndex })) : child
    /* The destination's OWN activation callbacks (c.93468 (1)). Slot would
       compose them with the element's and invoke them AFTER the cloned guard —
       stopping propagation does not stop a second function in the same
       handler — so while disabled they are consumed here: click and auxiliary
       click never call them, Enter and Space never call the keydown capture,
       and every other key still does. Enabled, they compose as before. */
    const { onClickCapture: _click, onAuxClickCapture: _aux, onKeyDownCapture: ownKeyCapture, ...passThrough } = rest
    const slotProps = disabled
      ? {
        ...passThrough,
        onKeyDownCapture: ownKeyCapture
          ? (event: KeyboardEvent<HTMLElement>) => { if (!ACTIVATION_KEYS.has(event.key)) ownKeyCapture(event) }
          : undefined,
      }
      : rest
    return (
      <Slot ref={ref} {...slotProps} {...(native || tabIndex === undefined ? {} : { tabIndex })}
        className={cx(s.destination, className)}>
        {supplied}
      </Slot>
    )
  }
  const { asChild: _asChild, current = false, disabled = false, className, children, type, ...rest } = props
  /* The kit's own button IS the root: when disabled, its props keep the same
     promise as a supplied root's (N6). */
  if (disabled) assertPassiveProps('button', rest as Record<string, unknown>, true, 'the destination\'s own props on its disabled <button>')
  return (
    <button ref={ref as Ref<HTMLButtonElement>} type={type ?? 'button'} {...rest}
      className={cx(s.destination, className)}
      aria-current={current && !disabled ? 'page' : undefined}
      disabled={disabled}>
      {children}
    </button>
  )
}

export const NavigationDestination = forwardRef(render) as unknown as NavigationDestinationComponent
NavigationDestination.displayName = 'NavigationDestination'
